/**
 * Subscription plans and feature gating.
 *
 * The single source of truth for what a plan costs and what it unlocks. Nothing anywhere else
 * in the codebase may test a plan code directly — call `can()` or `limitFor()`. That rule is
 * what makes a pricing change a one-file edit instead of an archaeology exercise, and it is
 * checked in review.
 *
 * See `.context/06-PRICING-AND-PLANS.md` for the commercial reasoning, including why automatic
 * reminders are the paywall and why the mobile app is bundled rather than sold separately.
 */

import { formatMoney, type Minor } from "./money";

export type PlanCode = "starter" | "growth" | "agency" | "enterprise";

export type Feature =
  | "manual_reminders"
  | "automatic_reminders"
  | "sms_channel"
  | "tenant_portal"
  | "owner_portal"
  | "mobile_app"
  | "offline_inspections"
  | "online_payments"
  | "bank_reconciliation"
  | "custom_templates"
  | "portfolio_scoping"
  | "public_listings"
  | "data_transfer"
  | "api_access"
  | "remove_branding";

export interface Plan {
  code: PlanCode;
  name: string;
  blurb: string;
  /** Price per month in minor units of `PRICING_CURRENCY`. */
  monthly: Minor;
  /** Annual price. Ten months for the price of twelve — the standard lever. */
  yearly: Minor;
  limits: {
    /**
     * How many properties the agency may hold.
     *
     * **Both this and `units` apply, whichever bites first.** One property can be a block of two
     * hundred flats, so a unit ceiling on its own is not a size limit at all; and an agency with
     * forty lock-up shops on forty streets is plainly a bigger business than one with a single
     * house, however few units each of those shops turns out to be. Metering only one of the two
     * left a way round whichever one was chosen.
     *
     * Only the free plan is metered. Nothing above it has ever had a property ceiling and adding
     * one now would invent a wall for people who are already paying — the plans get their size
     * from `units`, which is the number an agency recognises as its own.
     */
    properties: number; // Infinity for unlimited
    units: number; // Infinity for unlimited
    staff: number;
    /**
     * How many units may stand on the public marketplace at once.
     *
     * The free plan gets one and nothing above it is metered, because this is the way in: an
     * agency that has never paid us anything can put its best property in front of strangers,
     * take the enquiries, and decide from that whether the rest of the product is worth paying
     * for. A free tier that cannot be *seen* by anybody is a demo, not a way in.
     *
     * Counted on the switch, not on what a stranger can currently load — see `listingLimit` in
     * `apps/api/src/plan.ts` for why that distinction is load-bearing.
     */
    listings: number;
    emailsPerMonth: number;
    auditRetentionDays: number;
  };
  features: Feature[];
  /** The one the pricing page highlights. */
  featured?: boolean;
}

export const PRICING_CURRENCY = "NGN";

/** The single number every price is derived from. Change this, and the whole table moves. */
const BASE: Minor = 1_500_000; // ₦15,000

const monthlyToYearly = (monthly: Minor): Minor => monthly * 10;

export const PLANS: Record<PlanCode, Plan> = {
  starter: {
    code: "starter",
    name: "Starter",
    blurb: "For one property you still chase by hand — and a place on the market for it, free.",
    monthly: 0,
    yearly: 0,
    /**
     * One property, ten units, one listing.
     *
     * **A deliberate tightening**, from no property ceiling at all, twenty-five units and three
     * listings. The old free plan was not a way in; it was a small product, and an agency could
     * run a real book on it indefinitely without ever meeting a reason to pay.
     *
     * One of each is enough to prove every part of the product on real records — a real advert, a
     * real enquiry landing in the book, a real invoice raised against a real tenant — and small
     * enough that a second building is the upgrade moment. Ten units is the one number with any
     * slack in it, because one property that happens to be a block of flats still has to be
     * usable or the free plan tests nothing.
     *
     * **This lands on agencies that are already here**, and it does not take anything from them:
     * everything over the allowance is kept, kept being billed, and padlocked on the screen rather
     * than removed. See `planLocks` below, and `.context/15-PLAN-LIMITS-AND-GATING.md`.
     */
    limits: { properties: 1, units: 10, staff: 2, listings: 1, emailsPerMonth: 50, auditRetentionDays: 30 },
    /**
     * The free plan can publish. This is a change of position, not an oversight: the marketplace
     * used to be a paid feature nothing actually enforced, so every agency had it and no agency
     * had been told it was worth anything. Metered at one, it is both the giveaway and the
     * reason to leave.
     */
    features: ["manual_reminders", "public_listings"],
  },
  growth: {
    code: "growth",
    name: "Growth",
    blurb: "Reminders send themselves, and your agents get the mobile app.",
    monthly: BASE * 2,
    yearly: monthlyToYearly(BASE * 2),
    limits: { properties: Number.POSITIVE_INFINITY, units: 150, staff: 8, listings: Number.POSITIVE_INFINITY, emailsPerMonth: 2_000, auditRetentionDays: 365 },
    features: [
      "manual_reminders",
      "automatic_reminders",
      "sms_channel",
      "tenant_portal",
      "owner_portal",
      "mobile_app",
      "online_payments",
      "custom_templates",
      "data_transfer",
      "public_listings",
      "remove_branding",
    ],
    featured: true,
  },
  agency: {
    code: "agency",
    name: "Agency",
    blurb: "Everything, for a team running several hundred units.",
    monthly: BASE * 6,
    yearly: monthlyToYearly(BASE * 6),
    limits: { properties: Number.POSITIVE_INFINITY, units: 600, staff: 25, listings: Number.POSITIVE_INFINITY, emailsPerMonth: 10_000, auditRetentionDays: 365 * 3 },
    features: [
      "manual_reminders",
      "automatic_reminders",
      "sms_channel",
      "tenant_portal",
      "owner_portal",
      "mobile_app",
      "offline_inspections",
      "online_payments",
      "bank_reconciliation",
      "custom_templates",
      "data_transfer",
      "portfolio_scoping",
      "public_listings",
      "api_access",
      "remove_branding",
    ],
  },
  enterprise: {
    code: "enterprise",
    name: "Enterprise",
    blurb: "Unlimited units, a named contact, and terms that fit you.",
    monthly: -1, // quote only
    yearly: -1,
    limits: {
      properties: Number.POSITIVE_INFINITY,
      units: Number.POSITIVE_INFINITY,
      staff: Number.POSITIVE_INFINITY,
      listings: Number.POSITIVE_INFINITY,
      emailsPerMonth: Number.POSITIVE_INFINITY,
      auditRetentionDays: 365 * 7,
    },
    features: [
      "manual_reminders",
      "automatic_reminders",
      "sms_channel",
      "tenant_portal",
      "owner_portal",
      "mobile_app",
      "offline_inspections",
      "online_payments",
      "bank_reconciliation",
      "custom_templates",
      "data_transfer",
      "portfolio_scoping",
      "public_listings",
      "api_access",
      "remove_branding",
    ],
  },
};

export const PLAN_ORDER: PlanCode[] = ["starter", "growth", "agency", "enterprise"];

/** New signups get the top plan for a fortnight, then fall back to Starter rather than a wall. */
export const TRIAL_PLAN: PlanCode = "agency";
export const TRIAL_DAYS = 14;

export function plan(code: PlanCode): Plan {
  return PLANS[code] ?? PLANS.starter;
}

export function can(code: PlanCode, feature: Feature): boolean {
  return plan(code).features.includes(feature);
}

export function limitFor(code: PlanCode, limit: keyof Plan["limits"]): number {
  return plan(code).limits[limit];
}

/* ------------------------------------------------------- what a move costs */

/** How often the agency pays. A period is a term bought, never a description of a plan. */
export type BillingPeriod = "monthly" | "yearly";

/** Is this a plan code at all? For anything that has read one off the wire. */
export function isPlanCode(value: unknown): value is PlanCode {
  return typeof value === "string" && (PLAN_ORDER as string[]).includes(value);
}

/** The same question for a period. */
export function isBillingPeriod(value: unknown): value is BillingPeriod {
  return value === "monthly" || value === "yearly";
}

/**
 * What a plan costs for a term, in minor units of `PRICING_CURRENCY`.
 *
 * **Negative means there is no price to quote** — see `isQuoteOnly`. Zero means free. Every caller
 * that is about to charge somebody must tell those two apart, because charging nothing and
 * refusing to charge are opposite outcomes.
 */
export function priceOf(code: PlanCode, period: BillingPeriod = "monthly"): Minor {
  const chosen = plan(code);
  return period === "yearly" ? chosen.yearly : chosen.monthly;
}

/**
 * A plan whose price is a conversation rather than a number.
 *
 * Enterprise carries `-1` for exactly this. It can never be bought from a screen: it is arranged
 * with us and granted from the platform console, which leaves a record of who granted it.
 */
export function isQuoteOnly(code: PlanCode): boolean {
  return priceOf(code, "monthly") < 0;
}

/** Where a plan sits on the ladder. The order of `PLAN_ORDER` is the order of the prices. */
function rank(code: PlanCode): number {
  const index = PLAN_ORDER.indexOf(code);
  return index === -1 ? 0 : index;
}

/**
 * What moving from one plan to another *is*.
 *
 * - `none` — the same plan on the same terms. Nothing to do and nothing to charge.
 * - `down` — a cheaper plan, or the free one. **Never requires a payment.** An agency that wants
 *   to spend less must be able to; making a downgrade wait on a card is how somebody ends up
 *   paying for a plan they asked to leave.
 * - `up` — a dearer plan, or the same plan on a longer term. It is a purchase, so it happens when
 *   the money does and not before.
 * - `quote` — a plan with no price. It cannot be bought at all, only granted.
 *
 * This is the only place the ladder is compared. Every caller asks this rather than testing a plan
 * code, which is what keeps a pricing change a one-file edit.
 */
export type PlanMove = "none" | "down" | "up" | "quote";

export function planMove(
  from: PlanCode,
  to: PlanCode,
  fromPeriod: BillingPeriod = "monthly",
  toPeriod: BillingPeriod = "monthly",
): PlanMove {
  if (rank(to) < rank(from)) return "down";
  if (to === from) {
    /* The same plan, so only the term can have changed — and a free plan has no term worth
       charging for. Buying a year having been paying by the month is a purchase; dropping back to
       the month is not, and the period already paid for is never shortened by one. */
    if (toPeriod === fromPeriod || priceOf(to, toPeriod) <= 0) return "none";
    return priceOf(to, toPeriod) > priceOf(from, fromPeriod) ? "up" : "down";
  }
  if (isQuoteOnly(to)) return "quote";
  return "up";
}

/** The cheapest plan that includes a feature — what the upgrade prompt must name. */
export function cheapestPlanWith(feature: Feature): Plan | null {
  for (const code of PLAN_ORDER) {
    if (can(code, feature)) return plan(code);
  }
  return null;
}

export interface LimitCheck {
  allowed: boolean;
  used: number;
  limit: number;
  /** True once within 10% of the ceiling, so the banner appears before the wall does. */
  nearLimit: boolean;
  message?: string;
  /**
   * The same refusal in the shape every other shut door in the product wears.
   *
   * `null` while the agency is under the ceiling. See `Gate` — the interface reads this to draw a
   * padlock and open the pricing modal, and reads nothing else.
   */
  gate: Gate | null;
}

export function checkLimit(code: PlanCode, limit: keyof Plan["limits"], used: number): LimitCheck {
  const max = limitFor(code, limit);
  const allowed = used < max;
  const message = allowed ? undefined : limitMessage(limit, max);
  return {
    allowed,
    used,
    limit: max,
    nearLimit: Number.isFinite(max) && used >= max * 0.9,
    message,
    gate: allowed ? null : gateForLimit(code, limit, message ?? ""),
  };
}

/**
 * What happens to the records above the allowance, said in one sentence.
 *
 * **The copy this replaces promised the opposite.** It said "no record of yours is affected either
 * way", which stopped being true the day records over the allowance started carrying a padlock. A
 * promise that quietly stops being true is worse than one that was never made, so the sentence is
 * now the truth, in the same plain register: nothing is deleted, the money keeps moving, and the
 * screen is what closes.
 *
 * Written without menace on purpose. This is read by somebody who has paid us nothing and is
 * deciding whether we are worth paying — a sentence that sounds like a threat loses that argument
 * even when every word of it is accurate.
 */
const padlockNote = (max: number) =>
  `Nothing is deleted and nothing stops being billed — where there are more than ${max}, the ${max} most recently added stay open and the older ones are padlocked until you move up.`;

function limitMessage(limit: keyof Plan["limits"], max: number): string {
  switch (limit) {
    case "properties":
      return `Your plan covers ${max} ${max === 1 ? "property" : "properties"}. You cannot add another until you move up a plan. ${padlockNote(max)}`;
    case "units":
      return `Your plan covers ${max} units. You cannot add another until you move up a plan. ${padlockNote(max)}`;
    case "staff":
      return `Your plan covers ${max} staff logins.`;
    /*
      The refusal has to say what to do, because there are two ways out and only one of them
      costs anything. An agency told only "you have reached your limit" assumes it has to pay;
      taking one advert down is free and is usually what they actually want, since the property
      they are trying to publish is the one they care about today.

      "Nothing already published comes down" survives the padlock and is still literally true: a
      listing is a switch on a unit, the switch is never flipped by us, and the marketplace goes on
      showing the advert. What is padlocked is the unit's own screen, which is why the sentence
      after it now says so rather than claiming no record is affected.
    */
    case "listings":
      return `Your plan puts ${max} ${max === 1 ? "property" : "properties"} on the public marketplace at a time. Take one off the market to make room for this one, or move up a plan. Nothing already published comes down. Records above what your plan covers are kept and padlocked rather than removed — you can still see them, and everything they are owed goes on being billed.`;
    case "emailsPerMonth":
      return `Your plan includes ${max.toLocaleString()} emails a month. Automatic reminders pause at the limit; you can still send manually.`;
    default:
      return `You have reached a limit on your plan.`;
  }
}

/* ------------------------------------------------------------------ the shape of a shut door */

/**
 * Why something is shut — and there are exactly three, because the fourth is not a gate at all.
 *
 * | Reason | What the interface does |
 * |---|---|
 * | `plan` | Draw it, padlocked. Pressing it opens the pricing modal. |
 * | `limit` | Draw it, padlocked. Pressing it opens the pricing modal. |
 * | `module` | Draw it, padlocked. Pressing it starts a conversation, not a checkout. |
 *
 * **A permission is deliberately absent from that list.** Something a role may not do stays
 * *hidden*, exactly as it is today, because a junior without edit rights cannot fix a padlock with
 * money — only their manager can, and a padlock they will never be able to open is noise. Fill a
 * staff screen with those and people learn to ignore every padlock on it, including the ones that
 * would have earned a subscription.
 *
 * So the rule for anything serving this shape: **compute the permission filter first and drop what
 * fails it; then attach a `Gate` to what is left.** A `Gate` in a payload always means "show this,
 * shut". No `Gate` and no key at all means "this caller is not entitled to know it exists".
 */
export type GateReason = "plan" | "limit" | "module";

export interface Gate {
  reason: GateReason;
  /**
   * What is shut, in the vocabulary of the reason: a `Feature` for `plan`, a key of
   * `Plan["limits"]` for `limit`, a module code for `module`. Enough for the interface to key a
   * padlock off without parsing the message.
   */
  subject: string;
  /** One sentence for the padlock's tooltip and the modal's first line. Already user-facing. */
  message: string;
  /** The plan the agency is on. Named, not compared — nothing outside this file compares a code. */
  current: { code: PlanCode; name: string };
  /**
   * The cheapest plan that opens it, when money is the answer.
   *
   * `null` for a module, where it is not: modules are held, not bought up a ladder, and offering
   * somebody a plan that would not give them the thing is worse than offering nothing.
   */
  needed: { code: PlanCode; name: string } | null;
}

const named = (code: PlanCode) => ({ code, name: plan(code).name });

/** A feature this plan does not include. `null` when it does — callers spread the result. */
export function gateForFeature(code: PlanCode, feature: Feature): Gate | null {
  if (can(code, feature)) return null;
  const needed = cheapestPlanWith(feature);
  return {
    reason: "plan",
    subject: feature,
    message: `${FEATURE_LABELS[feature]} is included from the ${needed?.name ?? "next"} plan upwards. Your agency is on ${plan(code).name}.`,
    current: named(code),
    needed: needed ? named(needed.code) : null,
  };
}

/** A ceiling this plan has reached. Built by `checkLimit`; exported for callers holding a count. */
export function gateForLimit(code: PlanCode, limit: keyof Plan["limits"], message?: string): Gate {
  const needed = PLAN_ORDER.find((other) => limitFor(other, limit) > limitFor(code, limit));
  return {
    reason: "limit",
    subject: limit,
    message: message || limitMessage(limit, limitFor(code, limit)),
    current: named(code),
    needed: needed ? named(needed) : null,
  };
}

/**
 * A module the organisation does not hold.
 *
 * Padlocked rather than hidden, and for a reason that has nothing to do with plans: a letting-only
 * agency seeing a shut Development section is a good advert for a thing it did not know we sold.
 * `needed` is null because no plan on the table would open it — `orgs.modules` is the purchase.
 *
 * Takes the label rather than the code so this file stays the only one that knows about plans and
 * `modules.ts` stays the only one that knows about modules. The caller reads `MODULE_INFO`.
 */
export function gateForModule(code: PlanCode, module: string, label: string): Gate {
  return {
    reason: "module",
    subject: module,
    message: `${label} is not part of what your agency holds. Nothing here is lost by it — talk to us and we can add it.`,
    current: named(code),
    needed: null,
  };
}

export function formatPlanPrice(code: PlanCode, period: "monthly" | "yearly" = "monthly"): string {
  const p = plan(code);
  const amount = period === "monthly" ? p.monthly : p.yearly;
  if (amount < 0) return "Talk to us";
  if (amount === 0) return "Free";
  return formatMoney(amount, PRICING_CURRENCY) + (period === "monthly" ? "/month" : "/year");
}

/** What a year on the annual plan saves against paying monthly. Shown beside the toggle. */
export function annualSaving(code: PlanCode): Minor {
  const p = plan(code);
  if (p.monthly <= 0) return 0;
  return p.monthly * 12 - p.yearly;
}

export const FEATURE_LABELS: Record<Feature, string> = {
  manual_reminders: "Manual reminders",
  automatic_reminders: "Automatic reminders",
  sms_channel: "SMS and WhatsApp reminders",
  tenant_portal: "Tenant portal",
  owner_portal: "Owner portal and statements",
  mobile_app: "Mobile app for iOS and Android",
  offline_inspections: "Offline inspections",
  online_payments: "Online payment links",
  bank_reconciliation: "Bank statement matching",
  data_transfer: "Import and export your records",
  custom_templates: "Custom document and message templates",
  portfolio_scoping: "Per-agent portfolios",
  public_listings: "Public listings page",
  api_access: "REST API and webhooks",
  remove_branding: "Your branding on tenant emails",
};

/* ------------------------------------------------------------------------- the padlock itself */

/**
 * The record kinds a plan ceiling can padlock.
 *
 * **`listings` is deliberately not one of them.** A listing is a switch on a unit, not a record in
 * its own right: the unit carries the padlock, the switch is never flipped by us, and the advert
 * goes on standing on the public marketplace exactly as it was. A `LockableLimit` of `listings`
 * would be an invitation for somebody to write the one line of code this whole feature exists to
 * prevent — quietly taking an agency's advert down to enforce a pricing decision.
 */
export type LockableLimit = "properties" | "units";

/** The two fields the decision needs. Anything else about the record is not its business. */
export interface LockableRecord {
  id: string;
  createdAt: Date | string | number;
}

export interface LockView {
  limit: LockableLimit;
  /** How many stay open. `Infinity` on every plan that does not meter this kind. */
  allowance: number;
  /** How many the agency holds. Reported honestly, never clamped to the allowance. */
  total: number;
  /** True when there are more than the allowance, and therefore at least one padlock to draw. */
  over: boolean;
  /**
   * The ids that stay open, newest first.
   *
   * `null` is the common case and means **every one of them is open** — nothing is metered, or the
   * agency is under its allowance. It is deliberately not an array of every id: a payload that
   * lists six hundred open units to say "nothing is locked" is a payload nobody will keep sending.
   */
  unlocked: string[] | null;
  /** The ids behind the padlock, newest first. Empty whenever `over` is false. */
  locked: string[];
}

/**
 * Newest created first, and the ordering is the whole of the decision.
 *
 * The user's choice, and the reasoning is that recent work is active work: an agency dropping to
 * free keeps whatever it is in the middle of, and what goes behind the padlock is the long-running
 * tenancy nobody has touched this quarter. That is also precisely why the rule below the fold —
 * that the ledger never consults any of this — matters as much as it does.
 *
 * `id` breaks a tie so the answer is stable between two requests. Two units created in the same
 * millisecond by one bulk run is not a rare case; it is the normal case.
 */
const newestFirst = (a: LockableRecord, b: LockableRecord) => {
  const at = new Date(a.createdAt).getTime();
  const bt = new Date(b.createdAt).getTime();
  if (at !== bt) return bt - at;
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
};

/**
 * Which of an agency's records are open, and which carry a padlock.
 *
 * ## THE PADLOCK NEVER TOUCHES THE LEDGER. READ THIS BEFORE CALLING IT.
 *
 * A locked unit still has a tenant, and that tenant still owes rent. So:
 *
 * - **Invoices still raise** on their due dates.
 * - **Reminders still send.**
 * - **Payments still post, and still allocate.**
 * - **Statements, reports, arrears and every total still count it.**
 *
 * What this function decides is whether the *screen* opens — whether a row is pressable, whether
 * the edit form is reachable, whether pressing it offers a padlock and the pricing modal instead.
 * Nothing else. It is not a filter, it is not a `WHERE` clause, and it is not a reason to leave a
 * record out of a sum.
 *
 * **The failure this warning exists to prevent**: an agency drops to the free plan, fifty-five
 * tenancies quietly stop being billed because somebody added `isUnlocked()` to the invoicing run
 * "for consistency", and they find out a month later with a hole in their books that we made. That
 * is not a bug we could apologise for. It is the product taking money out of a customer's business
 * to make a point about pricing.
 *
 * So: **no caller in the invoicing, reminder, payment, allocation or reporting paths may import
 * this.** If a change needs it there, the change is wrong. `tools/verify-plan-locks.mjs` proves
 * the ledger still moves for a locked record, and it is the test to keep green rather than to
 * loosen.
 *
 * (It is also pure — plain records in, ids out. It cannot read a database even if somebody wanted
 * it to, which is the second reason it lives here rather than in the API.)
 */
export function planLocks(code: PlanCode, limit: LockableLimit, records: LockableRecord[]): LockView {
  const allowance = limitFor(code, limit);
  const total = records.length;

  if (!Number.isFinite(allowance) || total <= allowance) {
    return { limit, allowance, total, over: false, unlocked: null, locked: [] };
  }

  const ordered = [...records].sort(newestFirst).map((record) => record.id);
  return {
    limit,
    allowance,
    total,
    over: true,
    unlocked: ordered.slice(0, allowance),
    locked: ordered.slice(allowance),
  };
}

/**
 * Whether one record's screen opens.
 *
 * The same decision as `planLocks`, asked one record at a time — the browser holds a list and the
 * API holds a row, and both have to reach the same answer or the padlock disagrees with the server
 * about which door is shut. Everything is unlocked when the plan does not meter the kind, which is
 * what makes this safe to call on every row of every list.
 */
export function isUnlocked(
  code: PlanCode,
  limit: LockableLimit,
  records: LockableRecord[],
  id: string,
): boolean {
  const view = planLocks(code, limit, records);
  return view.unlocked === null ? true : view.unlocked.includes(id);
}

/**
 * The padlock as a `Gate`, for a screen that has one to draw.
 *
 * Separate from `gateForLimit` because the two say different things and are read at different
 * moments: that one is the refusal when somebody tries to add the next record, this one is what a
 * padlock says about a record that already exists. Both point at the same plan.
 */
export function gateForLock(code: PlanCode, limit: LockableLimit): Gate {
  const max = limitFor(code, limit);
  const noun = limit === "properties" ? (max === 1 ? "property" : "properties") : "units";
  const needed = PLAN_ORDER.find((other) => limitFor(other, limit) > max);
  return {
    reason: "limit",
    subject: limit,
    message: `Your plan opens ${max} ${noun} — the ${max} most recently added. This one is still here, still yours and still being billed; moving up a plan opens it again.`,
    current: named(code),
    needed: needed ? named(needed) : null,
  };
}

/**
 * The writes a padlock actually refuses, named rather than described.
 *
 * There are five, and the list is short on purpose: **every one of them is something a person does
 * on a screen**, and none of them is a step in billing anybody. `edit` and `delete` are the padlock
 * doing the one job it has; `publish`, `let` and `extend` are the three ways a padlocked record
 * could otherwise be made *more* valuable to the agency while the plan says it is above the line.
 *
 * What is deliberately not in this list is the whole of the ledger. Recording a payment against a
 * padlocked unit, allocating it, raising its invoice, renewing its tenancy, terminating it,
 * sending its reminder, counting it in arrears — none of those is a `LockedWrite`, because none of
 * them is a screen. See `planLocks` below for the failure that rule exists to prevent.
 */
export type LockedWrite = "edit" | "delete" | "publish" | "let" | "extend";

/**
 * What each refusal says, in a sentence that names the fix rather than the rule.
 *
 * Three things every one of them has to do, in this order:
 *
 * 1. Say *why* it is shut, in the agency's own terms — this record is above what the plan opens,
 *    and the ones the plan opens are the newest.
 * 2. Say *what to do about it*. "You have reached a limit" is a support ticket; "move up a plan to
 *    edit it" is an answer. There is exactly one way out of a padlock and it should be said.
 * 3. **Say that nothing has been lost and the money is still moving.** This is the sentence that
 *    stops the refusal being frightening. Somebody who has just been refused an edit to a flat
 *    they have let for nine years needs to be told, in the same breath, that the tenancy is intact
 *    and the rent is still being chased. It is also simply true, and it is the whole design.
 *
 * Written without menace. The reader is deciding whether we are worth paying, and a refusal that
 * sounds like a threat loses that argument even when every word of it is accurate.
 */
const lockedWriteFix: Record<LockedWrite, string> = {
  edit: "Move up a plan to edit it",
  delete: "Move up a plan to delete it",
  publish: "Move up a plan to put it on the market",
  let: "Move up a plan to record a tenancy on it",
  extend: "Move up a plan to add a unit to it",
};

/**
 * The refusal an interactive write gets when the record behind it is padlocked.
 *
 * Distinct from `gateForLock`, which is what the padlock *says on the screen* before anybody
 * presses anything. This is what comes back when a write is actually refused, so it names the one
 * action that was refused rather than describing the padlock in general. Both are the same
 * `reason`/`subject` pair and both point at the same plan, so a browser holding either can open
 * the pricing modal from it without knowing which it got.
 */
export function gateForLockedWrite(code: PlanCode, limit: LockableLimit, write: LockedWrite): Gate {
  const max = limitFor(code, limit);
  const subject = limit === "properties" ? "property" : "unit";
  const noun = limit === "properties" ? (max === 1 ? "property" : "properties") : "units";
  const needed = PLAN_ORDER.find((other) => limitFor(other, limit) > max);
  /* "the 1 most recently added" is not a sentence anybody says. The singular is the free plan's
     one property, so it is the case a reader is most likely to meet. */
  const opens = max === 1 ? `1 ${noun}, the most recently added` : `${max} ${noun}, the ${max} most recently added`;
  return {
    reason: "limit",
    subject: limit,
    message:
      `This ${subject} is above what your plan opens — ${plan(code).name} opens ${opens}. ` +
      `${lockedWriteFix[write]} — nothing has been lost, and rent on it is still being billed.`,
    current: named(code),
    needed: needed ? named(needed) : null,
  };
}

/**
 * Every feature, at runtime.
 *
 * Derived from the labels rather than written out again, so a feature can never be added to one
 * list and forgotten in the other — which would show a padlock with no words on it, or worse, no
 * padlock at all over something the plan does not include.
 */
export const FEATURES = Object.keys(FEATURE_LABELS) as Feature[];
